/**
 * Превью-микс в браузере (docs/RECORDING.md «Превью в браузере»,
 * docs/DUBBING.md «Ключевое техническое ограничение»).
 *
 * Режим mute — как в MVP:
 *
 *   AudioContext
 *     ├── MediaElementSource(<video>) ── Gain(originalGain) ──┐
 *     ├── BufferSource(take1) ── Gain(take1.gain) ───────────┤── destination
 *     └── BufferSource(take2) ── Gain(take2.gain) ───────────┘
 *
 * Режим replace — видео играет с `muted`, основа берётся из M&E-дорожки,
 * декодированной в AudioBuffer:
 *
 *   AudioContext
 *     ├── BufferSource(M&E) ──── Gain(meGain) ───────────────┐
 *     ├── BufferSource(take1) ── Gain(take1.gain) ───────────┤── destination
 *     └── BufferSource(take2) ── Gain(take2.gain) ───────────┘
 *
 * Расписание и обработка seek/pause общие для обоих режимов (см. clips.ts).
 * Декодированные дубли кешируются по `take.id`, дорожки — в trackBuffers.
 */

import { ClipScheduler, dbToLinear } from './clips'
import type { Clip } from './clips'
import { loadTrackBuffer, peekTrackBuffer } from './trackBuffers'

export type PreviewMode = 'mute' | 'replace'

export type PreviewItem = {
  takeId: string
  url: string
  /** Абсолютный таймкод видео, на котором должен начаться дубль. */
  startAt: number
  gainDb: number
}

/** Дорожка-основа для режима replace. */
export type PreviewTrack = { id: string; url: string }

export type MeStatus = 'absent' | 'loading' | 'ready' | 'error'

export type PreviewState = {
  enabled: boolean
  mode: PreviewMode
  /** Сколько дублей декодируется прямо сейчас. */
  loading: number
  failed: string[]
  meStatus: MeStatus
  meError: string | null
}

export class PreviewMixer {
  private readonly video: HTMLVideoElement
  private readonly scheduler: ClipScheduler
  private ctx: AudioContext | null = null
  private mediaSource: MediaElementAudioSourceNode | null = null
  private originalGainNode: GainNode | null = null

  private readonly buffers = new Map<string, AudioBuffer>()
  private readonly pending = new Set<string>()
  private readonly failed = new Set<string>()
  private items: PreviewItem[] = []

  private mode: PreviewMode = 'mute'
  private meTrack: PreviewTrack | null = null
  private meBuffer: AudioBuffer | null = null
  private meStatus: MeStatus = 'absent'
  private meError: string | null = null
  private meLoadingId: string | null = null
  private meGainDb = 0

  private enabled = false
  private originalGainDb = -18
  private disposed = false
  /** Исходное значение video.muted — восстанавливаем, чтобы не залипло. */
  private mutedBefore: boolean | null = null
  private onStateChange: ((state: PreviewState) => void) | null = null

  constructor(video: HTMLVideoElement) {
    this.video = video
    this.scheduler = new ClipScheduler({
      video,
      getContext: () => this.ctx,
      getClips: () => this.collectClips(),
      isActive: () => this.enabled && !this.disposed,
    })
  }

  setStateListener(listener: ((state: PreviewState) => void) | null): void {
    this.onStateChange = listener
    this.emitState()
  }

  isEnabled(): boolean {
    return this.enabled
  }

  /** Включение требует жеста пользователя — AudioContext иначе не стартует. */
  async setEnabled(enabled: boolean): Promise<void> {
    if (this.disposed || this.enabled === enabled) return
    this.enabled = enabled
    if (enabled) {
      const ctx = this.ensureContext()
      if (ctx.state === 'suspended') await ctx.resume()
      void this.ensureBuffers()
      void this.ensureMeBuffer()
    } else {
      this.scheduler.stopAll()
    }
    this.applyOutput()
    this.scheduler.reschedule()
    this.emitState()
  }

  setMode(mode: PreviewMode): void {
    if (this.disposed || this.mode === mode) return
    this.mode = mode
    this.applyOutput()
    if (this.enabled) {
      void this.ensureMeBuffer()
      this.scheduler.reschedule()
    }
    this.emitState()
  }

  /** Дорожка-основа для replace. null — дорожки нет, рендер и превью не пойдут. */
  setMeTrack(track: PreviewTrack | null): void {
    if (this.disposed) return
    if (this.meTrack?.id === track?.id) return
    this.meTrack = track
    this.meBuffer = null
    this.meLoadingId = null
    this.meError = null
    this.meStatus = track ? 'loading' : 'absent'
    this.applyOutput()
    if (this.enabled) void this.ensureMeBuffer()
    this.emitState()
  }

  setMeGainDb(db: number): void {
    this.meGainDb = db
    // Уже запланированные клипы держат свой gain — перепланируем.
    if (this.enabled && this.mode === 'replace') this.scheduler.reschedule()
  }

  setOriginalGainDb(db: number): void {
    this.originalGainDb = db
    this.applyOutput()
  }

  setItems(items: PreviewItem[]): void {
    if (this.disposed) return
    this.items = items
    for (const id of [...this.buffers.keys()]) {
      if (!items.some((item) => item.takeId === id)) this.buffers.delete(id)
    }
    if (!this.enabled) return
    void this.ensureBuffers()
    this.scheduler.reschedule()
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.scheduler.dispose()
    this.buffers.clear()
    this.meBuffer = null
    this.restoreMuted()
    this.onStateChange = null
    try {
      this.mediaSource?.disconnect()
      this.originalGainNode?.disconnect()
    } catch {
      // узлы могли быть уже отключены
    }
    const ctx = this.ctx
    this.ctx = null
    this.mediaSource = null
    this.originalGainNode = null
    if (ctx && ctx.state !== 'closed') void ctx.close().catch(() => undefined)
  }

  // ------------------------------------------------------------ внутренности

  private ensureContext(): AudioContext {
    if (this.ctx) return this.ctx
    const ctx = new AudioContext()
    this.ctx = ctx
    // MediaElementSource на один элемент можно создать только один раз за жизнь
    // элемента, поэтому держим его до dispose().
    this.mediaSource = ctx.createMediaElementSource(this.video)
    this.originalGainNode = ctx.createGain()
    this.mediaSource.connect(this.originalGainNode)
    this.originalGainNode.connect(ctx.destination)
    this.applyOutput()
    return ctx
  }

  private collectClips(): Clip[] {
    const clips: Clip[] = []
    if (this.mode === 'replace' && this.meBuffer) {
      clips.push({ buffer: this.meBuffer, startAt: 0, gainDb: this.meGainDb })
    }
    for (const item of this.items) {
      const buffer = this.buffers.get(item.takeId)
      if (!buffer) continue
      clips.push({ buffer, startAt: item.startAt, gainDb: item.gainDb })
    }
    return clips
  }

  /**
   * Громкость оригинала и `muted` у видео.
   *
   * В replace глушим элемент только когда M&E уже декодирована: иначе
   * пользователь получил бы полную тишину без объяснений.
   */
  private applyOutput(): void {
    const replacing = this.enabled && this.mode === 'replace' && this.meBuffer !== null
    if (replacing) {
      if (this.mutedBefore === null) this.mutedBefore = this.video.muted
      this.video.muted = true
    } else {
      this.restoreMuted()
    }
    const node = this.originalGainNode
    if (!node || !this.ctx) return
    let target = 1
    if (replacing) target = 0
    else if (this.enabled && this.mode === 'mute') target = dbToLinear(this.originalGainDb)
    node.gain.setTargetAtTime(target, this.ctx.currentTime, 0.02)
  }

  private restoreMuted(): void {
    if (this.mutedBefore === null) return
    this.video.muted = this.mutedBefore
    this.mutedBefore = null
  }

  private async ensureBuffers(): Promise<void> {
    const ctx = this.ctx
    if (!ctx) return
    const tasks: Array<Promise<void>> = []
    for (const item of this.items) {
      if (this.buffers.has(item.takeId) || this.pending.has(item.takeId)) continue
      if (this.failed.has(item.takeId)) continue
      this.pending.add(item.takeId)
      this.emitState()
      tasks.push(
        (async (): Promise<void> => {
          try {
            const res = await fetch(item.url)
            if (!res.ok) throw new Error(`HTTP ${res.status}`)
            const decoded = await ctx.decodeAudioData(await res.arrayBuffer())
            if (this.disposed) return
            this.buffers.set(item.takeId, decoded)
          } catch {
            this.failed.add(item.takeId)
          } finally {
            this.pending.delete(item.takeId)
            this.emitState()
          }
        })(),
      )
    }
    if (tasks.length === 0) return
    await Promise.all(tasks)
    if (this.disposed) return
    if (this.enabled && !this.video.paused) this.scheduler.reschedule()
  }

  /**
   * M&E декодируется целиком — для длинного файла это десятки секунд и сотни
   * мегабайт. Интерфейс при этом не блокируется: статус уходит в PreviewState.
   */
  private async ensureMeBuffer(): Promise<void> {
    if (this.mode !== 'replace') return
    const track = this.meTrack
    const ctx = this.ctx
    if (!track) {
      this.meStatus = 'absent'
      this.emitState()
      return
    }
    if (!ctx) return
    if (this.meBuffer) return
    const cached = peekTrackBuffer(ctx, track.id)
    if (cached) {
      this.meBuffer = cached
      this.meStatus = 'ready'
      this.applyOutput()
      this.emitState()
      this.scheduler.reschedule()
      return
    }
    if (this.meLoadingId === track.id) return
    this.meLoadingId = track.id
    this.meStatus = 'loading'
    this.meError = null
    this.emitState()
    try {
      const buffer = await loadTrackBuffer(ctx, track.id, track.url)
      if (this.disposed || this.meTrack?.id !== track.id) return
      this.meBuffer = buffer
      this.meStatus = 'ready'
    } catch (err) {
      if (this.disposed || this.meTrack?.id !== track.id) return
      this.meStatus = 'error'
      this.meError = err instanceof Error ? err.message : String(err)
    } finally {
      if (this.meLoadingId === track.id) this.meLoadingId = null
      if (!this.disposed) {
        this.applyOutput()
        this.emitState()
        this.scheduler.reschedule()
      }
    }
  }

  private emitState(): void {
    this.onStateChange?.({
      enabled: this.enabled,
      mode: this.mode,
      loading: this.pending.size,
      failed: [...this.failed],
      meStatus: this.meStatus,
      meError: this.meError,
    })
  }
}
